import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { Icon } from "@components/Icon";
import { StyledContainer } from "@components/Styled/Container";
import type { ResultsProps } from "@usereactify/search";
import React from "react";
import tw, { styled } from "twin.macro";

type NumberedPaginationProps = React.ComponentProps<
  NonNullable<ResultsProps["renderPaginationNumbered"]>
> & {
  scrollTo: (position: number) => void;
};

type PaginationNumberItemProps = {
  page: number;
  currentPage: number;
  buildPagePath: NumberedPaginationProps["buildPagePath"];
  handlePageChange: NumberedPaginationProps["handlePageChange"];
  scrollTo: (position: number) => void;
  dataTestId: string;
};

type PaginationNavButtonProps = {
  type: "next" | "prev";
  toPage: number;
  buildPagePath: NumberedPaginationProps["buildPagePath"];
  onClickHandler:
    | NumberedPaginationProps["handleNextPage"]
    | NumberedPaginationProps["handlePreviousPage"];
  scrollTo: (position: number) => void;
};

type PaginationBreakItemProps = {
  dataTestId: string;
};

const Container = tw(StyledContainer)`
  flex flex-col items-start relative
`;
const PaginatedWrapper = tw.ul`
  flex mb-maxi mt-maxi
`;
const PaginatedNav = tw.a`
  w-maxi h-maxi flex items-center justify-center
  [> aria-disabled]:(text-grey cursor-default)
`;
const PaginatedItem = withFocusRing(styled.a`
  ${tw`flex items-center justify-center bg-transparent border w-maxi h-maxi mx-pico border-grey text-primary text-body`}
  ${({ selected }: { selected: boolean }) =>
    selected
      ? tw`border-none bg-primary text-white`
      : tw`hover:bg-tertiary hover:border-none`}
`);

const PaginationBreak = tw.span`
  w-maxi h-maxi mx-pico flex items-center justify-center
`;
const NextIcon = tw(Icon)`
`;
const PreviousIcon = tw(Icon)`
  transform rotate-180
`;

const paginationNavSettings = {
  prev: {
    label: "Goto previous page",
    icon: <PreviousIcon name="chevron" size="xxs" />,
  },
  next: {
    label: "Goto next page",
    icon: <NextIcon name="chevron" size="xxs" />,
  },
};

const PaginationNavButton = ({
  type,
  toPage,
  buildPagePath,
  onClickHandler,
  scrollTo,
}: PaginationNavButtonProps) => {
  return (
    <li>
      <PaginatedNav
        aria-label={paginationNavSettings[type].label}
        rel={type}
        href={buildPagePath(toPage)}
        onClick={(event) => {
          onClickHandler(event);
          scrollTo(20);
        }}
        data-testid={type}
      >
        {paginationNavSettings[type].icon}
      </PaginatedNav>
    </li>
  );
};

const PaginationBreakItem = ({ dataTestId }: PaginationBreakItemProps) => (
  <li>
    <PaginationBreak data-testid={dataTestId}>...</PaginationBreak>
  </li>
);

const PaginationNumberItem = ({
  page,
  currentPage,
  buildPagePath,
  handlePageChange,
  scrollTo,
  dataTestId,
}: PaginationNumberItemProps) => {
  const isCurrentPage = page === currentPage;
  const ariaLabel = isCurrentPage
    ? `Current Page ${page + 1}`
    : `Goto Page  ${page + 1}`;
  const href = isCurrentPage ? undefined : buildPagePath(page + 1);
  const onClickHandler = (event) => {
    handlePageChange(page, event);
    scrollTo(20);
  };

  return (
    <li>
      <PaginatedItem
        selected={isCurrentPage}
        href={href}
        aria-label={ariaLabel}
        aria-current={isCurrentPage}
        onClick={isCurrentPage ? undefined : onClickHandler}
        data-testid={dataTestId}
      >
        {page + 1}
      </PaginatedItem>
    </li>
  );
};

const PaginationNav = ({
  currentPage,
  buildPagePath,
  handlePreviousPage,
  handleNextPage,
  pagesToShow,
  totalPages,
  handlePageChange,
  scrollTo,
}: NumberedPaginationProps) => {
  const show = {
    prev: currentPage >= 1,
    first: !pagesToShow.includes(0),
    leadingBreak: !pagesToShow.includes(1) && pagesToShow?.length > 1,
    laggingBreak:
      !pagesToShow.includes(totalPages - 2) && pagesToShow?.length > 1,
    last: !pagesToShow.includes(totalPages - 1),
    next: currentPage + 1 < totalPages,
  };

  return (
    <nav aria-label="Pagination">
      <PaginatedWrapper>
        {show.prev && (
          <PaginationNavButton
            key="prev"
            type="prev"
            toPage={currentPage}
            buildPagePath={buildPagePath}
            onClickHandler={handlePreviousPage}
            scrollTo={scrollTo}
          />
        )}
        {show.first && (
          <PaginationNumberItem
            key="first"
            page={0}
            currentPage={currentPage}
            buildPagePath={buildPagePath}
            handlePageChange={handlePageChange}
            scrollTo={scrollTo}
            dataTestId="firstpagequicklink"
          />
        )}
        {show.leadingBreak && (
          <PaginationBreakItem key="leadbreak" dataTestId="leadbreak" />
        )}
        {pagesToShow.map((page) => (
          <PaginationNumberItem
            key={page}
            page={page}
            currentPage={currentPage}
            buildPagePath={buildPagePath}
            handlePageChange={handlePageChange}
            scrollTo={scrollTo}
            dataTestId={`page ${page + 1}`}
          />
        ))}
        {show.laggingBreak && (
          <PaginationBreakItem key="lagbreak" dataTestId="lagbreak" />
        )}
        {show.last && (
          <PaginationNumberItem
            key="last"
            page={totalPages - 1}
            currentPage={currentPage}
            buildPagePath={buildPagePath}
            handlePageChange={handlePageChange}
            scrollTo={scrollTo}
            dataTestId="lastpagequicklink"
          />
        )}
        {show.next && (
          <PaginationNavButton
            key="next"
            type="next"
            toPage={currentPage + 2}
            buildPagePath={buildPagePath}
            onClickHandler={handleNextPage}
            scrollTo={scrollTo}
          />
        )}
      </PaginatedWrapper>
    </nav>
  );
};

const NumberedPagination = (props: NumberedPaginationProps) => {
  if (props.totalPages <= 1) {
    return;
  }
  return (
    <Container width="full">
      <PaginationNav {...props}></PaginationNav>
    </Container>
  );
};

export default NumberedPagination;
